import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SpeciesDetailModal = ({ species, onClose }) => {
  if (!species) return null;

  const getThreatColor = (level) => {
    const colors = {
      'Critical': 'text-error',
      'Endangered': 'text-warning',
      'Vulnerable': 'text-accent',
      'Stable': 'text-success'
    };
    return colors?.[level] || 'text-muted-foreground';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-semibold text-foreground">{species?.commonName}</h2>
          <button 
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground animation-organic"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="relative h-80 rounded-lg overflow-hidden">
              <Image
                src={species?.image}
                alt={species?.imageAlt}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm text-muted-foreground mb-1">Scientific Name</h3>
                <p className="text-lg font-medium text-foreground italic">{species?.scientificName}</p>
              </div>

              <div>
                <h3 className="text-sm text-muted-foreground mb-1">Conservation Status</h3>
                <p className={`text-lg font-semibold ${getThreatColor(species?.threatLevel)}`}>
                  {species?.threatLevel}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm text-muted-foreground mb-1">Population</h3>
                  <p className="text-base font-medium text-foreground">{species?.population}</p>
                </div>
                <div>
                  <h3 className="text-sm text-muted-foreground mb-1">Habitat</h3>
                  <p className="text-base font-medium text-foreground">{species?.habitat}</p>
                </div>
              </div>

              <div>
                <h3 className="text-sm text-muted-foreground mb-1">Primary Threat</h3>
                <p className="text-base text-foreground">{species?.primaryThreat}</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{species?.fullDescription}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Conservation Actions</h3>
              <ul className="space-y-2">
                {species?.conservationActions?.map((action, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{action}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Research Data</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Last Survey</p>
                  <p className="text-base font-medium text-foreground">{species?.lastSurvey}</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Population Trend</p>
                  <p className="text-base font-medium text-foreground capitalize">{species?.populationTrend}</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Protected Areas</p>
                  <p className="text-base font-medium text-foreground">{species?.protectedAreas}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t border-border">
            <Button variant="default" iconName="Download" iconPosition="left" className="flex-1">
              Download Report
            </Button>
            <Button variant="outline" iconName="Share2" iconPosition="left" className="flex-1">
              Share Data
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeciesDetailModal;